import { useEffect, useState } from "react";
import { CheckCircle2, XCircle, Banknote, RefreshCcw, AlertTriangle, X } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr, inr, fmtDateTime } from "../../lib/api";
import { FullScreenLoader } from "../../context/AuthContext";

const W_STATUS = {
  REQUESTED: "bg-amber-100 text-amber-700", UNDER_REVIEW: "bg-amber-100 text-amber-700",
  APPROVED: "bg-blue-100 text-blue-700", PROCESSING: "bg-blue-100 text-blue-700",
  PAID: "bg-emerald-100 text-emerald-700", REJECTED: "bg-red-100 text-red-600", FAILED: "bg-red-100 text-red-600",
};

const NEXT_ACTIONS = {
  REQUESTED: ["approve", "reject"], UNDER_REVIEW: ["approve", "reject"],
  APPROVED: ["process", "pay", "fail"], PROCESSING: ["pay", "fail"],
};

export default function AdminFranchiseWithdrawals() {
  const [rows, setRows] = useState(null);
  const [recon, setRecon] = useState(null);
  const [exceptions, setExceptions] = useState([]);
  const [action, setAction] = useState(null); // {withdrawal, action}
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/admin/franchises/withdrawal-queue").then(({ data }) => setRows(data)).catch((e) => { toast.error(fmtErr(e)); setRows([]); });
    api.get("/admin/franchise-exceptions").then(({ data }) => setExceptions(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const runRecon = async () => {
    setRecon(null);
    try {
      const { data } = await api.get("/admin/franchise-reconciliation");
      setRecon(data);
      if (!data.issues?.length) toast.success("Reconciliation clean — no issues");
      else toast.warning(`${data.issues.length} reconciliation issue(s) found`);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const doAction = async () => {
    if (!action) return;
    setBusy(true);
    try {
      await api.post(`/admin/franchise-withdrawals/${action.withdrawal.withdrawal_id}/action`, {
        action: action.action,
        payment_reference: action.action === "pay" ? input : "",
        reason: ["reject", "fail"].includes(action.action) ? input : "",
      });
      toast.success(`Withdrawal ${action.action}d`);
      setAction(null);
      setInput("");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  if (!rows) return <FullScreenLoader label="Loading payout queue" />;

  return (
    <div className="space-y-5" data-testid="admin-franchise-withdrawals-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-shipany-navy">Franchise Payouts</h1>
          <p className="text-sm text-slate-500">Approve → process → mark paid with the bank/UPI reference.</p>
        </div>
        <button onClick={runRecon} data-testid="run-reconciliation-btn"
          className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-shipany-navy hover:border-shipany-blue">
          <RefreshCcw className="h-4 w-4" /> Run Reconciliation
        </button>
      </div>

      {recon && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="reconciliation-panel">
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">Reconciliation — {recon.commission_count} commissions · {recon.wallet_count} wallets</h2>
          {recon.issues?.length ? (
            <div className="mt-3 space-y-2">
              {recon.issues.map((i, idx) => (
                <div key={idx} className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-xs text-red-700" data-testid={`recon-issue-${idx}`}>
                  <AlertTriangle className="h-4 w-4" /> <span className="font-bold">{i.kind}</span> · {i.id} {i.expected_invoice != null ? `· expected ${inr(i.expected_invoice)} vs ${inr(i.actual_invoice)}` : ""}
                </div>
              ))}
            </div>
          ) : <p className="mt-3 text-xs font-semibold text-emerald-600" data-testid="recon-clean">All checks passed — wallets match ledger, no duplicates, no orphans.</p>}
          {(recon.wallet_checks || []).length > 0 && (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full min-w-[560px] text-xs">
                <thead><tr className="border-b border-slate-100 text-left font-bold uppercase text-slate-400"><th className="py-2 pr-4">Franchise</th><th className="py-2 pr-4 text-right">Available</th><th className="py-2 pr-4 text-right">Ledger Net</th><th className="py-2 pr-4 text-right">Locked</th><th className="py-2">Checks</th></tr></thead>
                <tbody>
                  {recon.wallet_checks.map((wc) => (
                    <tr key={wc.franchise_id} className="border-b border-slate-50" data-testid={`recon-wallet-${wc.franchise_id}`}>
                      <td className="mono py-2 pr-4">{wc.franchise_id}</td>
                      <td className="mono py-2 pr-4 text-right">{inr(wc.available)}</td>
                      <td className="mono py-2 pr-4 text-right">{inr(wc.ledger_net)}</td>
                      <td className="mono py-2 pr-4 text-right">{inr(wc.locked)}</td>
                      <td className="py-2">{wc.balance_ok && wc.locked_ok ? <span className="font-bold text-emerald-600">OK</span> : <span className="font-bold text-red-500">MISMATCH</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full min-w-[860px] text-sm">
          <thead>
            <tr className="border-b border-slate-100 text-left text-xs font-bold uppercase tracking-wide text-slate-400">
              <th className="px-5 py-3">Requested</th><th className="px-5 py-3">Franchise</th><th className="px-5 py-3 text-right">Amount</th>
              <th className="px-5 py-3">Bank</th><th className="px-5 py-3">Status</th><th className="px-5 py-3">Reference</th><th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((x) => (
              <tr key={x.withdrawal_id} className="border-b border-slate-50" data-testid={`payout-row-${x.withdrawal_id}`}>
                <td className="px-5 py-3.5 text-xs text-slate-500">{fmtDateTime(x.requested_at)}</td>
                <td className="px-5 py-3.5 text-xs font-semibold">{x.franchise_name || x.franchise_id}</td>
                <td className="mono px-5 py-3.5 text-right text-xs font-bold">{inr(x.amount)}</td>
                <td className="px-5 py-3.5 text-xs">{x.bank_snapshot ? `${x.bank_snapshot.bank_name} · ${x.bank_snapshot.account_number_masked}` : "—"}</td>
                <td className="px-5 py-3.5"><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${W_STATUS[x.status] || "bg-slate-100 text-slate-500"}`} data-testid={`payout-status-${x.withdrawal_id}`}>{x.status}</span></td>
                <td className="mono px-5 py-3.5 text-xs">{x.payment_reference || "—"}</td>
                <td className="px-5 py-3.5 text-right">
                  <div className="flex justify-end gap-1.5">
                    {(NEXT_ACTIONS[x.status] || []).map((a) => (
                      <button key={a} onClick={() => { setAction({ withdrawal: x, action: a }); setInput(""); }} data-testid={`payout-${a}-${x.withdrawal_id}`}
                        className={`rounded-lg px-2.5 py-1 text-[10px] font-bold uppercase ${a === "pay" ? "bg-emerald-600 text-white" : a === "approve" || a === "process" ? "bg-shipany-blue text-white" : "bg-red-100 text-red-600"}`}>
                        {a}
                      </button>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={7} className="px-5 py-10 text-center text-sm text-slate-400" data-testid="payouts-empty">No withdrawal requests.</td></tr>}
          </tbody>
        </table>
      </div>

      {exceptions.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="franchise-exceptions-panel">
          <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-500"><AlertTriangle className="h-4 w-4 text-amber-500" /> Finance Exceptions</h2>
          <div className="mt-3 space-y-2">
            {exceptions.slice(0, 20).map((e) => (
              <div key={e.exception_id} className="rounded-xl border border-amber-100 bg-amber-50 px-4 py-2.5 text-xs text-amber-800" data-testid={`exception-${e.exception_id}`}>
                <span className="font-bold">{e.kind}</span> · franchise {e.franchise_id} · {fmtDateTime(e.created_at)}
              </div>
            ))}
          </div>
        </div>
      )}

      {action && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="payout-action-modal">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-bold capitalize text-shipany-navy">{action.action} withdrawal</h2>
              <button onClick={() => setAction(null)} data-testid="payout-action-close" className="text-slate-400"><X className="h-5 w-5" /></button>
            </div>
            <p className="mt-2 text-sm text-slate-500">{action.withdrawal.franchise_name} · <span className="mono font-bold">{inr(action.withdrawal.amount)}</span></p>
            {action.action === "pay" && (
              <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Bank / UPI payment reference *" data-testid="payout-reference-input"
                className="mt-4 w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:border-shipany-blue focus:outline-none" />
            )}
            {["reject", "fail"].includes(action.action) && (
              <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Reason" data-testid="payout-reason-input"
                className="mt-4 w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:border-shipany-blue focus:outline-none" />
            )}
            <button onClick={doAction} disabled={busy} data-testid="payout-action-confirm"
              className={`mt-4 flex w-full items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-bold text-white disabled:opacity-60 ${["reject", "fail"].includes(action.action) ? "bg-red-500" : "bg-emerald-600"}`}>
              {action.action === "pay" ? <Banknote className="h-4 w-4" /> : ["reject", "fail"].includes(action.action) ? <XCircle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
              {busy ? "Processing…" : `Confirm ${action.action}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
